import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Container,
  Button,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Link,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StagingBanner from './StagingBanner';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    navigate('/login');
    handleClose();
  };

  const handleProfile = () => {
    navigate('/perfil');
    handleClose();
  };

  const menuItems = [
    { text: 'Análisis de Síntomas', path: '/nueva-consulta', roles: ['admin', 'medico', 'paciente'] },
    { text: 'Análisis de Exámenes', path: '/analisis-examenes', roles: ['admin', 'medico', 'paciente'] },
    { text: 'Historial de Casos', path: '/casos', roles: ['admin', 'medico', 'paciente'] },
    { text: 'Panel Médico', path: '/panel-medico', roles: ['medico', 'admin'] },
    { text: 'Panel Admin', path: '/admin', roles: ['admin'] }
  ].filter(item => item.roles.includes(usuario.rol));

  // Función para determinar si mostrar el botón de volver
  const shouldShowBackButton = () => {
    const noBackButtonRoutes = ['/dashboard', '/', '/login', '/registro'];
    return !noBackButtonRoutes.includes(location.pathname);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StagingBanner />
      <Box sx={{ pt: '40px' }}>
        <AppBar 
          position="static" 
          elevation={0}
          sx={{
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 1rem' }}>
              {/* Logo, Botón Volver y nombre */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {shouldShowBackButton() && (
                  <Tooltip title="Volver">
                    <IconButton
                      onClick={() => navigate(-1)}
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                    }
                  }} 
                  onClick={() => navigate('/dashboard')}
                >
                  <LocalHospitalIcon sx={{ display: 'flex', mr: 1, color: 'primary.main', fontSize: '2rem' }} />
                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      letterSpacing: '.3rem',
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontSize: '1.5rem'
                    }}
                  >
                    TRIAGE
                  </Typography>
                </Box>
              </Box>

              {/* Enlaces de navegación */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    sx={{
                      color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      position: 'relative',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      backgroundColor: location.pathname === item.path ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        width: location.pathname === item.path ? '100%' : '0%',
                        height: '2px',
                        backgroundColor: 'primary.main',
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        color: 'primary.main',
                      },
                      '&:hover::after': {
                        width: '100%',
                      },
                      transition: 'all 0.2s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>

              {/* Avatar y menú de perfil */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 2 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      textShadow: '0px 0px 1px rgba(0,0,0,0.1)'
                    }}
                  >
                    {usuario?.nombre} {usuario?.apellidos}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.primary',
                      textTransform: 'capitalize',
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      px: 1,
                      py: 0.2,
                      borderRadius: 1,
                      fontWeight: 500
                    }}
                  >
                    {usuario?.rol}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleMenu}
                  color="inherit"
                  size="large"
                  edge="end"
                  aria-label="cuenta del usuario"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                >
                  <Avatar
                    alt={usuario?.nombre}
                    src={usuario?.imagenUrl || ''}
                    sx={{ width: 40, height: 40 }}
                  />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {usuario.nombre || 'Usuario'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usuario.email || ''}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem onClick={handleProfile}>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Mi Perfil" />
                  </MenuItem>
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Cerrar Sesión" />
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </Box>
      <Box component="main" sx={{ flex: 1 }}>
        {children}
      </Box>
      
      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: '#f8fafc',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            flexWrap: 'wrap'
          }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/terminos-condiciones')}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
            >
              Términos y Condiciones
            </Link>
            <Typography variant="body2" color="text.secondary">
              |
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/privacidad')}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
            >
              Política de Privacidad
            </Link>
            <Typography variant="body2" color="text.secondary">
              |
            </Typography>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} AI Symtomatix
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout; 